/*eslint-disable*/
import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, Typography, CircularProgress } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart"; // Import BarChart
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers-pro/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

import FastAPIClient from "../../client";
import config from "../../config";
import StatCard from "../LayoutComponents/StatCard";

const client = new FastAPIClient(config);

dayjs.extend(isBetween);

type UserActivity = {
    user_id: string;
    first_name: string;
    last_name: string;
    activity_date: string;
    event_count: number;
    team_name: string;
    warehouse_name: string;
};

interface DashboardUserLoginsProps {
    selectedUser: string;
    selectedTeam: string;
    selectedWarehouse: string;
    timePeriod: string;
    dateRange: [Dayjs | null, Dayjs | null];
}

const DashboardUserLogins: React.FC<DashboardUserLoginsProps> = ({
    selectedUser,
    selectedTeam,
    selectedWarehouse,
    timePeriod,
    dateRange,
}) => {
    const [data, setData] = useState<UserActivity[] | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchData();
    }, [dateRange]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await client.fetchUserActivity(
                dateRange[0]?.format("YYYY-MM-DD"),
                dateRange[1]?.format("YYYY-MM-DD")
            );
            setData(response.results);
        } catch (error) {
            console.error("Error fetching user activity:", error);
        } finally {
            setLoading(false);
        }
    };

    const filteredData = useMemo(() => {
        if (!data) return [];

        let filtered = data;

        if (selectedTeam !== "all") {
            filtered = filtered.filter((d) => d.team_name === selectedTeam);
        }

        if (selectedWarehouse !== "all") {
            filtered = filtered.filter(
                (d) => d.warehouse_name === selectedWarehouse
            );
        }

        // Filter by date range
        if (dateRange[0] && dateRange[1]) {
            filtered = filtered.filter((d) => {
                const activityDate = dayjs(d.activity_date);
                return activityDate.isBetween(dateRange[0], dateRange[1], 'day', '[]');
            });
        }

        if (selectedUser === "all") {
            const aggregateMap = new Map<string, Set<string>>();
            filtered.forEach((entry) => {
                if (entry.event_count > 0) {
                    const date = entry.activity_date;
                    const userId = entry.user_id;
                    if (!aggregateMap.has(date)) {
                        aggregateMap.set(date, new Set());
                    }
                    aggregateMap.get(date)?.add(userId);
                }
            });

            return Array.from(aggregateMap.entries()).map(([date, users]) => ({
                date,
                value: users.size,
            }));
        }

        return filtered
            .filter((d) => `${d.first_name} ${d.last_name}` === selectedUser)
            .map((d) => ({
                date: d.activity_date,
                value: d.event_count > 0 ? 1 : 0,
            }));
    }, [data, selectedUser, selectedTeam, selectedWarehouse, dateRange]);

    useEffect(() => {
        console.log(filteredData);
    }, [filteredData]);

    const chartData = useMemo(() => {
        if (!filteredData) return { labels: [], values: [] };

        const sortedData = [...filteredData]
            .filter((entry) => {
                const day = dayjs(entry.date).day(); // 0 = Sunday, 6 = Saturday
                return day !== 0 && day !== 6;
            })
            .sort((a, b) => a.date.localeCompare(b.date));

        const labels = sortedData.map((entry) => entry.date);
        const values = sortedData.map((entry) => entry.value);

        return { labels, values };
    }, [filteredData]);

    const maxUserCount = useMemo(() => {
        if (!data || !dateRange[0] || !dateRange[1]) return 0;

        if (selectedUser !== "all") {
            return 1;
        }

        const userSet = new Set<string>();

        data.forEach((entry) => {
            const isInDateRange = dayjs(entry.activity_date).isBetween(
                dateRange[0],
                dateRange[1],
                null,
                "[]"
            );

            const matchesTeam =
                selectedTeam === "all" || entry.team_name === selectedTeam;

            const matchesWarehouse =
                selectedWarehouse === "all" ||
                entry.warehouse_name === selectedWarehouse;

            if (isInDateRange && matchesTeam && matchesWarehouse) {
                userSet.add(entry.user_id);
            }
        });

        // Fallback to count all matching users if none were found in date range
        if (userSet.size === 0) {
            data.forEach((entry) => {
                const matchesTeam =
                    selectedTeam === "all" || entry.team_name === selectedTeam;
                const matchesWarehouse =
                    selectedWarehouse === "all" ||
                    entry.warehouse_name === selectedWarehouse;

                if (matchesTeam && matchesWarehouse) {
                    userSet.add(entry.user_id);
                }
            });
        }

        return userSet.size;
    }, [data, dateRange, selectedUser, selectedTeam, selectedWarehouse]);

    const averageConnectionPercentage = useMemo(() => {
        if (!chartData.values.length || maxUserCount === 0) return 0;
        const totalConnections = chartData.values.reduce(
            (acc, val) => acc + val,
            0
        );
        const average = totalConnections / chartData.values.length;
        return (average / maxUserCount) * 100;
    }, [chartData.values, maxUserCount]);

    return (
        <StatCard
            title="Connexions quotidiennes"
            value={`${averageConnectionPercentage.toFixed(1)}%`}
            subtitle="Taux moyen de connexion quotidien"
            loading={loading}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <BarChart
                    borderRadius={10}
                    xAxis={[
                        {
                            data: chartData.labels,
                            scaleType: "band",
                            valueFormatter: (value: string) => {
                                const date = dayjs(value);
                                const day = date.date(); // This gives you the day without leading zero
                                const month = date.format("MMM");
                                const capitalizedMonth =
                                    month.charAt(0).toUpperCase() +
                                    month.slice(1);
                                return `${day} ${capitalizedMonth}`; // e.g., "2 Avr"
                            },
                        },
                    ]}
                    series={[
                        // Series for the main data (connexions)
                        {
                            data: chartData.values,
                            label: "Connexions",
                            // color: "#2196f3", // Green color for the Connexions series
                            stack: "stack1", // Stack with the next series
                        },
                        // Series for the difference between the data value and 20
                        {
                            data: chartData.values.map((value) =>
                                Math.max(maxUserCount - value, 0)
                            ),
                            label: "Difference",
                            color: "#f5f5f5", // Orange color for the difference part
                            stack: "stack1", // Stack with the main series
                        },
                    ]}
                    grid={{ vertical: false, horizontal: true }}
                    height={200}
                    sx={{
                        // Hide the Y-axis completely
                        // Show only Y-axis labels (tick labels)
                        "& .MuiChartsAxis-left .MuiChartsAxis-line": {
                            display: "none",
                        },
                        "& .MuiChartsAxis-left .MuiChartsAxis-tick": {
                            display: "none",
                        },
                        "& .MuiChartsAxis-left .MuiChartsAxis-tickLabel": {
                            fill: "#000000", // Set label color
                            fontSize: 12,
                            fontFamily: "Roboto",
                        },
                        // Dashed horizontal grid lines with light gray color
                        "& .MuiChartsGrid-horizontalLine": {
                            stroke: "lightgray",
                            strokeDasharray: "4 4",
                        },

                        // Font for all axis tick labels
                        "& .MuiChartsAxis-tickContainer .MuiChartsAxis-tickLabel":
                            {
                                fontFamily: "Roboto",
                            },
                        // Bottom label styles (X-axis)
                        "& .MuiChartsAxis-bottom .MuiChartsAxis-tickLabel": {
                            strokeWidth: "0.5",
                            fill: "#000000",
                        },
                        // Bottom Axis line styles
                        "& .MuiChartsAxis-bottom .MuiChartsAxis-line": {
                            stroke: "#000000",
                            strokeWidth: 0.4,
                        },
                        "& .MuiChartsBar-bar": {
                            stroke: "darkgray", // Dark gray border
                            strokeWidth: 2, // Border width
                            strokeDasharray: "4 4", // Dashed border style
                        },
                    }}
                />
            </LocalizationProvider>
        </StatCard>
    );
};

export default DashboardUserLogins;
